<template>
  <div
    style="gap: 1rem"
    class="f-column"
    :class="[{ gw_loading: loading }]"
  >
    <SolfigrIcmTournamentStructurePrefillBtns :namespace />

    <div
      v-if="hasType"
      class="d-flex f-wrap"
      style="gap: 1rem"
    >
      <div class="f-column">
        <GwTitleTooltip
          :title="$t(`solfigr.icm.tournament_type`)"
          :tooltip="$t(`solfigr.icm.tournament_type_tooltip`)"
          style="margin: 0 0 0.25rem 0"
        />
        <GwBtns
          :options="['regular', 'bounty']"
          class="gwbtns_horizontal_grey mr-auto gw_loading_text"
          style="
            margin-right: 0.5rem;
            border-radius: 4px;
            background: var(--clr-black-1-3);
            --gwbtn-clr-back: var(--clr-black-1-3);
            --gwbtn-clr-active-white: var(--clr-black-3);
            --gwbtn-size: 1rem;
            --gwbtn-height: 1.875rem;
            --gwbtns-padding: 2px;
          "
          :value="icmTournament.type === 'NORMAL' ? 'regular' : 'bounty'"
          tstPrefix="tournament_types"
          @click="
            (_e, item) => {
              if (item === 'regular') {
                updateValue('type', conf.tournament_types.default)
              } else {
                updateValue('type', conf.tournament_bounty_types.default)
              }
            }
          "
        >
          <template #default="{ item }">
            {{ $t(`solfigr.icm.${item}`) }}
          </template>
        </GwBtns>
      </div>

      <div
        v-if="icmTournament.type !== 'NORMAL'"
        class="f-column"
      >
        <GwTitleTooltip
          :title="$t(`solfigr.icm.bounty_type`)"
          :tooltip="$t(`solfigr.icm.bounty_type_tooltip`)"
          style="margin: 0 0 0.25rem 0"
        />
        <GwBtns
          :options="conf.tournament_bounty_types.options"
          class="gwbtns_horizontal_grey mr-auto gw_loading_text"
          style="
            margin-right: 0.5rem;
            border-radius: 4px;
            background: var(--clr-black-1-3);
            --gwbtn-clr-back: var(--clr-black-1-3);
            --gwbtn-clr-active-white: var(--clr-black-3);
            --gwbtn-size: 1rem;
            --gwbtn-height: 1.875rem;
            --gwbtns-padding: 2px;
            --gwbtns-wrap: wrap;
          "
          :value="icmTournament.type"
          tstPrefix="tournament_types"
          @click="(_e, item) => updateValue('type', item)"
        >
          <template #default="{ item }">
            <span v-html="$t(`solfigr.icm.${item}`)"> </span>
          </template>
        </GwBtns>
      </div>

      <div
        v-if="conf.bounty_instant_percentage.is_active(icmTournament.type)"
        class="f-column"
        data-tst="bounty_instant_percentage"
      >
        <GwTitleTooltip
          :title="$t(`solfigr.icm.bounty_instant_percentage`)"
          :tooltip="$t(`solfigr.icm.bounty_instant_percentage_tooltip`)"
          style="margin: 0 0 0.25rem 0"
        />
        <div class="position-relative mr-auto f-center">
          <NumberInput
            class="gw_loading_text"
            :min="conf.bounty_instant_percentage.min"
            :max="conf.bounty_instant_percentage.max"
            :step="conf.bounty_instant_percentage.step"
            :decimals="conf.bounty_instant_percentage.decimals"
            :inputmode="conf.bounty_instant_percentage.inputmode"
            :hasMinus="false"
            :hasPlus="false"
            :hasWheelScroll="false"
            style="--input-width: 8rem"
            :value="
              conf.bounty_instant_percentage.inputFormatter(icmTournament.bounty_instant_percentage)
            "
            @input="
              value =>
                updateValue(
                  'bounty_instant_percentage',
                  conf.bounty_instant_percentage.inputParser(value),
                )
            "
          />
          <div
            class="clr-grey weight_500 text-medium"
            style="position: absolute; right: 0.5rem; z-index: 8"
          >
            {{ conf.bounty_instant_percentage.unit }}
          </div>
        </div>
      </div>
    </div>

    <SolfigrIcmPayoutsTable
      v-if="hasPayouts"
      data-tst="payout_levels_table"
      class="gw_loading_text flex-1"
      :icmTournament
      :max="maxPayouts || conf.payout_levels.max"
      :min="minPayouts || conf.payout_levels.min"
      @update:items="value => updateValue('payout_levels', value)"
    />
  </div>
</template>

<script>
import GwBtns from '@/components/lib/GwBtns'
import GwTitleTooltip from '@/components/Dialogs/UserSettingsDialog/GwTitleTooltip'
import NumberInput from '@/components/lib/NumberInput'
import { getSolfigrFields, getTournamentStructure } from '@/store/solfigr/solfigr_helpers'
import float_mix from '@/mixins/float_mix'
import SolfigrIcmPayoutsTable from '@/components/ConfigureSolution/SolfigrIcmPayoutsTable'
import SolfigrIcmTournamentStructurePrefillBtns from '@/components/ConfigureSolution/SolfigrIcmTournamentStructurePrefillBtns'

export default {
  name: 'SolfigrIcmTournamentStructure',
  components: {
    SolfigrIcmPayoutsTable,
    SolfigrIcmTournamentStructurePrefillBtns,
    GwBtns,
    GwTitleTooltip,
    NumberInput,
  },
  mixins: [float_mix],
  props: {
    namespace: { String, required: true },
    loading: Boolean,
    icmTournament: Object,
    hasType: { default: true, type: Boolean },
    hasPayouts: { default: true, type: Boolean },
    maxPayouts: Number,
    minPayouts: Number,
  },
  emits: ['update:input', 'update:icmTournament'],
  data() {
    this.conf = getSolfigrFields()
    return {}
  },
  methods: {
    updateValue(key, value) {
      const res = getTournamentStructure({
        ...this.icmTournament, //
        name: '',
        [key]: value,
      })

      // autocheck+correct
      if (res.total_entrants < res.payout_levels.length) {
        res.total_entrants = res.payout_levels.length
      }

      this.$emit('update:icmTournament', res)
    },
  },
}
</script>
